import { apiGet, apiPut } from '../psUtil';
import { getWindowPS } from '../GlobalTypes';
import type { CtaContent } from './types';
import type { CtaSchedule } from './scheduleUtils';

type CtaScheduleApi = {
	start: string | null;
	end: string | null;
};

type CtaContentApi = {
	version: number;
	banner_version?: number;
	banner_two_version?: number;
	banner_text: string;
	banner_link_url: string | null;
	banner_schedule_start: string | null;
	banner_schedule_end: string | null;
	banner_two_text: string;
	banner_two_link_url: string | null;
	banner_two_schedule_start: string | null;
	banner_two_schedule_end: string | null;
	popup_title: string;
	popup_body: string;
	popup_image_url: string | null;
	popup_schedule_start: string | null;
	popup_schedule_end: string | null;
	popup_two_title: string;
	popup_two_body: string;
	popup_two_image_url: string | null;
	popup_two_schedule_start: string | null;
	popup_two_schedule_end: string | null;
};

type CtaContentUpdateApi = {
	banner_text?: string;
	banner_link_url?: string | null;
	banner_schedule?: CtaScheduleApi;
	banner_two_text?: string;
	banner_two_link_url?: string | null;
	banner_two_schedule?: CtaScheduleApi;
	popup_title?: string;
	popup_body?: string;
	popup_image_url?: string | null;
	popup_schedule?: CtaScheduleApi;
	popup_two_title?: string;
	popup_two_body?: string;
	popup_two_image_url?: string | null;
	popup_two_schedule?: CtaScheduleApi;
};

type CtaUpdateResponse = {
	success: boolean;
	content: CtaContentApi;
};

export const mapCtaContentFromApi = (data: CtaContentApi): CtaContent => ({
	version: data.version,
	bannerVersion: data.banner_version ?? data.version,
	bannerTwoVersion: data.banner_two_version ?? data.version,
	bannerText: data.banner_text ?? '',
	bannerLinkUrl: data.banner_link_url,
	bannerScheduleStart: data.banner_schedule_start,
	bannerScheduleEnd: data.banner_schedule_end,
	bannerTwoText: data.banner_two_text ?? '',
	bannerTwoLinkUrl: data.banner_two_link_url ?? null,
	bannerTwoScheduleStart: data.banner_two_schedule_start ?? null,
	bannerTwoScheduleEnd: data.banner_two_schedule_end ?? null,
	popupTitle: data.popup_title ?? '',
	popupBody: data.popup_body ?? '',
	popupImageUrl: data.popup_image_url,
	popupScheduleStart: data.popup_schedule_start,
	popupScheduleEnd: data.popup_schedule_end,
	popupTwoTitle: data.popup_two_title ?? '',
	popupTwoBody: data.popup_two_body ?? '',
	popupTwoImageUrl: data.popup_two_image_url ?? null,
	popupTwoScheduleStart: data.popup_two_schedule_start ?? null,
	popupTwoScheduleEnd: data.popup_two_schedule_end ?? null,
});

const mapScheduleToApi = (schedule: CtaSchedule): CtaScheduleApi => ({
	start: schedule.start,
	end: schedule.end,
});

const mapCtaContentUpdatesToApi = (updates: Partial<CtaContent>): CtaContentUpdateApi => {
	const payload: CtaContentUpdateApi = {};

	if (updates.bannerText !== undefined) payload.banner_text = updates.bannerText;
	if (updates.bannerLinkUrl !== undefined) payload.banner_link_url = updates.bannerLinkUrl;
	if (updates.bannerScheduleStart !== undefined || updates.bannerScheduleEnd !== undefined) {
		payload.banner_schedule = mapScheduleToApi({
			start: updates.bannerScheduleStart ?? null,
			end: updates.bannerScheduleEnd ?? null,
		});
	}
	if (updates.bannerTwoText !== undefined) payload.banner_two_text = updates.bannerTwoText;
	if (updates.bannerTwoLinkUrl !== undefined) payload.banner_two_link_url = updates.bannerTwoLinkUrl;
	if (updates.bannerTwoScheduleStart !== undefined || updates.bannerTwoScheduleEnd !== undefined) {
		payload.banner_two_schedule = mapScheduleToApi({
			start: updates.bannerTwoScheduleStart ?? null,
			end: updates.bannerTwoScheduleEnd ?? null,
		});
	}
	if (updates.popupTitle !== undefined) payload.popup_title = updates.popupTitle;
	if (updates.popupBody !== undefined) payload.popup_body = updates.popupBody;
	if (updates.popupImageUrl !== undefined) payload.popup_image_url = updates.popupImageUrl;
	if (updates.popupScheduleStart !== undefined || updates.popupScheduleEnd !== undefined) {
		payload.popup_schedule = mapScheduleToApi({
			start: updates.popupScheduleStart ?? null,
			end: updates.popupScheduleEnd ?? null,
		});
	}

	if (updates.popupTwoTitle !== undefined) payload.popup_two_title = updates.popupTwoTitle;
	if (updates.popupTwoBody !== undefined) payload.popup_two_body = updates.popupTwoBody;
	if (updates.popupTwoImageUrl !== undefined) payload.popup_two_image_url = updates.popupTwoImageUrl;
	if (updates.popupTwoScheduleStart !== undefined || updates.popupTwoScheduleEnd !== undefined) {
		payload.popup_two_schedule = mapScheduleToApi({
			start: updates.popupTwoScheduleStart ?? null,
			end: updates.popupTwoScheduleEnd ?? null,
		});
	}

	return payload;
};

export const fetchCtaContent = async (): Promise<CtaContent> => {
	const response = await apiGet<CtaContentApi>('Cta');
	return mapCtaContentFromApi(response.data);
};

export const updateCtaContent = async (updates: Partial<CtaContent>): Promise<CtaContent> => {
	const response = await apiPut<CtaUpdateResponse>('Cta', mapCtaContentUpdatesToApi(updates));
	return mapCtaContentFromApi(response.data.content);
};

export const navigateBannerLink = (link: string) => {
	const trimmed = link.trim();
	if (!trimmed) return;

	if (trimmed.startsWith('#') || trimmed.startsWith('/')) {
		const content = trimmed.startsWith('/') ? `#${trimmed.slice(1)}` : trimmed;
		getWindowPS().ROOT.loadContent(content);
		return;
	}

	if ((/^https?:\/\//i).test(trimmed)) {
		window.open(trimmed, '_blank', 'noopener,noreferrer');
		return;
	}

	window.open(`https://${trimmed}`, '_blank', 'noopener,noreferrer');
};
